import { getStores } from '@littlebird/sdk';
import { toast } from 'sonner';

import { backendApis } from '@/backendApis';

import { electronApis } from '../electron-api';

import { buildRenamedSpeakerSegments } from './meetings-transcript.utils';
import { meetingStore } from './meetings.state';

import type { MeetingId } from './meetings.types';

const MAX_SPEAKER_LABEL_LENGTH = 100;
const RESERVED_SPEAKER_LABELS = new Set(['You', 'Other', 'Others', 'Unknown']);

export function isAssignableSpeakerLabel(label: string): boolean {
  return !RESERVED_SPEAKER_LABELS.has(label);
}

export function normalizeSpeakerLabel(label: string): string {
  return label
    .replace(/[[\]\r\n]/g, '')
    .trim()
    .slice(0, MAX_SPEAKER_LABEL_LENGTH);
}

export async function renameTranscriptSpeaker(
  meetingId: MeetingId,
  currentLabel: string,
  newLabel: string
): Promise<boolean> {
  const trimmedLabel = normalizeSpeakerLabel(newLabel);
  if (
    !trimmedLabel ||
    trimmedLabel === currentLabel ||
    !isAssignableSpeakerLabel(trimmedLabel)
  ) {
    return false;
  }

  if (meetingStore.isMeetingInRecordingLifecycle(meetingId)) {
    meetingStore.renameResolvedSpeaker(meetingId, currentLabel, trimmedLabel);
    return true;
  }

  try {
    const meetings = getStores().meetings;
    const { personifiedTranscript, currentLength } =
      await meetings.fetchMeetingTranscript(meetingId);
    if (!personifiedTranscript) return false;

    const segments = buildRenamedSpeakerSegments(
      personifiedTranscript,
      currentLabel,
      trimmedLabel
    );
    if (!segments || !segments.some((s) => s.speaker === trimmedLabel)) {
      return false;
    }

    await backendApis.meetingNotes.replaceTranscriptSuffix(
      meetingId,
      0,
      currentLength,
      segments,
      await electronApis.system.getDeviceId()
    );
    await backendApis.meetingNotes.ingestRemainingTranscript(meetingId);

    await syncRenamedTranscriptLocally(meetingId, currentLabel, trimmedLabel);
    return true;
  } catch (error) {
    console.warn('[meetings] Failed to rename transcript speaker:', error);
    toast.error('Failed to rename speaker. Please try again.');
    return false;
  }
}

async function syncRenamedTranscriptLocally(
  meetingId: MeetingId,
  currentLabel: string,
  newLabel: string
): Promise<void> {
  const meetings = getStores().meetings;
  try {
    const { personifiedTranscript } =
      await meetings.fetchMeetingTranscript(meetingId);
    if (personifiedTranscript !== null) {
      meetingStore.setCommittedTranscript(meetingId, personifiedTranscript);
    }
  } catch (error) {
    console.warn(
      '[meetings] Failed to refresh transcript after rename:',
      error
    );
  }

  const speakers = meetings.get(meetingId)?.speakers;
  if (speakers?.includes(currentLabel)) {
    await meetings
      .updateMeeting(meetingId, {
        speakers: speakers.map((speaker) =>
          speaker === currentLabel ? newLabel : speaker
        ),
      })
      .catch((error) => {
        console.warn('[meetings] Failed to update speakers list:', error);
      });
  }
}
